/**
 * شماره‌های خودم روی وب — قرینه‌ی
 * apps/mobile/src/features/contacts/MyPhonesCard.tsx.
 *
 * چرا حالا روی وب هم هست، در حالی که قبلاً عمداً نبود: دلیلِ قبلی «مرورگر
 * دفترچه‌ی تلفن نداره» بود، که مالِ *خوندنِ* مخاطبینه. ثبتِ شماره‌ی **خودم**
 * ربطی به دفترچه نداره — کاری می‌کنه که دوستانی که شماره‌ت رو دارن پیدات
 * کنن، و از مایگریشن 0060 با کدِ پیامکی تایید میشه؛ هر دوش توی مرورگر کار
 * می‌کنه.
 *
 * برخلافِ موبایل این‌جا toast نداریم؛ مثل بقیه‌ی صفحه‌های وب پیام توی خودِ
 * کارت می‌شینه.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { phoneRepo, isSmsUnconfiguredError } from '@/core/data/phoneRepo';
import { normalizePhone, phoneKey } from '@/core/lib/contacts';
import { t } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { Btn, Card, Chip, Field, Help } from '@/ui/kit';

/** فاصله‌ی اجباریِ سرور بینِ دو کد (مایگریشن 0060). */
const RESEND_SECONDS = 60;

export function MyPhonesCard() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  /** شماره‌ای که کد براش رفته. تا وقتی پره، کارت توی مرحله‌ی دومه. */
  const [awaiting, setAwaiting] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [msg, setMsg] = useState<string | null>(null);

  const phones = useQuery({ queryKey: ['myPhones'], queryFn: phoneRepo.mine });

  // شمارشِ معکوسِ «دوباره بفرست». بدون این، کاربر دکمه رو می‌زنه و سرور
  // بی‌توضیح throttled می‌ده.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['myPhones'] });
    qc.invalidateQueries({ queryKey: ['myPhone'] });
  };

  const done = () => { setAwaiting(null); setCode(''); setDraft(''); setCooldown(0); refresh(); };

  const sendCode = useMutation({
    mutationFn: (phone: string) => phoneRepo.startVerification(phone),
    onSuccess: (r, phone) => {
      if (r.status === 'mine') { setMsg(t('phoneAlreadyMine')); done(); return; }
      if (r.status === 'too_many') { setMsg(t('phoneCodeTooMany')); return; }
      // throttled یعنی کدِ قبلی هنوز زنده‌ست — همون مرحله‌ی دوم، فقط بدونِ
      // پیامکِ تازه. برگردوندنِ کاربر به مرحله‌ی اول کدی که دستشه رو بی‌فایده می‌کرد.
      setAwaiting(phone);
      setCooldown(r.status === 'throttled' ? r.retryAfter : RESEND_SECONDS);
      setMsg(r.status === 'sent'
        ? t('phoneCodeSent', { phone: normalizePhone(phone) ?? phone })
        : null);
    },
    onError: (e) => {
      reportError('web.phones.startVerification', e);
      setMsg(isSmsUnconfiguredError(e) ? t('phoneSmsUnavailable') : t('errorRetry'));
    },
  });

  const verify = useMutation({
    mutationFn: () => phoneRepo.verify(awaiting!, code),
    onSuccess: (r) => {
      switch (r.status) {
        case 'claimed': setMsg(t('myPhoneSaved')); done(); break;
        case 'taken': setMsg(t('phoneTakenOver')); done(); break;
        case 'mine': setMsg(t('phoneAlreadyMine')); done(); break;
        case 'wrong': setMsg(t('phoneCodeWrong', { n: r.left })); setCode(''); break;
        case 'expired': setMsg(t('phoneCodeExpired')); setCode(''); setCooldown(0); break;
        case 'locked': setMsg(t('phoneCodeLocked')); setCode(''); setCooldown(0); break;
        default: setMsg(t('phoneCodeNone')); setCode(''); setCooldown(0); break;
      }
    },
    onError: (e) => { reportError('web.phones.verify', e); setMsg(t('errorRetry')); },
  });

  const makeDefault = useMutation({
    mutationFn: (phone: string) => phoneRepo.setDefault(phone),
    onSuccess: () => { refresh(); setMsg(t('phoneDefaultSaved')); },
    onError: (e) => { reportError('web.phones.setDefault', e); setMsg(t('errorRetry')); },
  });

  const remove = useMutation({
    mutationFn: (phone: string) => phoneRepo.remove(phone),
    onSuccess: () => { refresh(); setMsg(t('phoneRemoved')); },
    onError: (e) => { reportError('web.phones.remove', e); setMsg(t('errorRetry')); },
  });

  const list = phones.data ?? [];

  return (
    <Card>
      {/* «هر شماره مالِ یک حساب است و تایید منتقلش می‌کند» (مایگریشن 0060)
          پشتِ «؟» — حالتِ خاص است و لازم نیست همیشه جلوی چشم باشد. */}
      <h3>{t('myPhoneTitle')}<Help text={t('phoneUniqueNote')} /></h3>
      <p className="dim">{list.length > 1 ? t('phonesHint') : t('myPhoneHint')}</p>

      {list.map((p) => (
        <div className="phone-row" key={p.value}>
          <span className="phone-value">{p.value}</span>
          <span className="chiprow">
            <Chip label={p.isDefault ? t('phoneDefault') : t('phoneMakeDefault')}
              selected={p.isDefault}
              onClick={() => { if (!p.isDefault) makeDefault.mutate(p.value); }} />
            <Chip label={t('remove')} selected={false}
              onClick={() => {
                if (window.confirm(t('phoneRemoveConfirm', { phone: p.value }))) {
                  remove.mutate(p.value);
                }
              }} />
          </span>
        </div>
      ))}

      {awaiting === null ? (
        <>
          <Field label={t('myPhoneTitle')} value={draft} onChange={setDraft}
            placeholder={t('myPhonePlaceholder')} autoComplete="tel" />
          <Btn label={sendCode.isPending ? t('saving') : t('phoneSendCode')}
            disabled={sendCode.isPending}
            onClick={() => {
              if (!phoneKey(draft)) { setMsg(t('myPhoneInvalid')); return; }
              setMsg(null);
              sendCode.mutate(draft);
            }} />
          <p className="dim">{t('phoneVerifyNote')}</p>
        </>
      ) : (
        <>
          <Field label={t('phoneCodeLabel')} value={code} onChange={setCode}
            placeholder={t('phoneCodeLabel')} autoComplete="one-time-code" />
          <Btn label={verify.isPending ? t('saving') : t('phoneVerify')}
            disabled={verify.isPending || code.trim().length < 6}
            onClick={() => verify.mutate()} />
          <div className="chiprow">
            <Chip selected={false}
              label={cooldown > 0 ? t('phoneResendIn', { n: cooldown }) : t('phoneResend')}
              onClick={() => { if (cooldown <= 0) sendCode.mutate(awaiting); }} />
            <Chip selected={false} label={t('phoneChangeNumber')} onClick={done} />
          </div>
        </>
      )}

      {msg ? <p className="dim">{msg}</p> : null}
    </Card>
  );
}
